import LegalPageLayout from "@/components/LegalPageLayout";

const TermsPage = () => (
  <LegalPageLayout title="Terms & Conditions" lastUpdated="April 2026" testId="terms-page">
    <p>
      These Terms and Conditions (&ldquo;<strong>Terms</strong>&rdquo;) govern your access to and use of the{" "}
      <strong>tots360</strong> platform (the &ldquo;<strong>Platform</strong>&rdquo;, &ldquo;<strong>Service</strong>&rdquo;,
      &ldquo;<strong>we</strong>&rdquo;, &ldquo;<strong>us</strong>&rdquo;), operated by <strong>Rev &amp; Dev Children Clinic</strong>,
      a medical establishment registered in India with its principal place of business at 2nd Floor, Fairmount Fortune One,
      Czech Colony, Sanath Nagar, Hyderabad — 500018, Telangana, India. By accessing or using the Platform you agree to be bound by these Terms.
    </p>

    <h2>1. Eligibility</h2>
    <p>
      The Service is intended for use by parents, guardians, healthcare professionals, schools, and authorised administrators.
      By registering, you represent that you are at least 18 years of age and have the legal capacity to enter into a binding
      contract. Accounts created on behalf of a minor must be operated by a parent or legal guardian.
    </p>

    <h2>2. Description of Service</h2>
    <p>
      tots360 provides AI-assisted child health-screening tools including the BoneAH bone-age assessment and the
      NIN-NutriGuide nutrition analysis. The Service generates informational reports in PDF format based on the data you submit.
      Reports are offered for <strong>informational purposes only</strong> and do not constitute medical advice, diagnosis or treatment.
      Always consult a qualified healthcare professional for clinical decisions.
    </p>

    <h2>3. Account Registration &amp; Security</h2>
    <ul>
      <li>You must provide accurate, current and complete information during registration and keep it updated.</li>
      <li>You are responsible for maintaining the confidentiality of your account credentials and for all activities that occur under your account.</li>
      <li>You must notify us immediately of any unauthorised access or breach of security.</li>
      <li>We reserve the right to suspend or terminate accounts that violate these Terms or applicable law.</li>
    </ul>

    <h2>4. Fees, Credits &amp; Payment</h2>
    <ul>
      <li>The Service is offered on a <strong>pay-per-report</strong> basis. Each PDF report costs <strong>₹1,499</strong> (India) or <strong>$29.99</strong> (international) per credit, subject to applicable taxes.</li>
      <li>Indian residents are charged <strong>18% Goods and Services Tax (GST)</strong> on the net amount, as required by Indian law.</li>
      <li>Payments within India are processed by <strong>Razorpay Software Private Limited</strong>; international payments are processed by <strong>Stripe, Inc.</strong> Your use of these gateways is subject to their respective terms.</li>
      <li>Discount coupons, if offered, apply only to the transaction for which they are validly used and cannot be redeemed for cash or combined with other offers unless expressly permitted.</li>
      <li>All fees are exclusive of bank charges, card fees, and currency-conversion costs that your bank may apply.</li>
    </ul>

    <h2>5. Permitted Use</h2>
    <p>You agree <strong>not</strong> to:</p>
    <ul>
      <li>Use the Service for any unlawful, fraudulent or harmful purpose.</li>
      <li>Upload personal data of any child without the verifiable consent of the child&rsquo;s parent or legal guardian.</li>
      <li>Reverse-engineer, copy, scrape, or otherwise attempt to extract the underlying source code, machine-learning models or proprietary data.</li>
      <li>Resell, sublicense, or otherwise commercially exploit the reports or platform output without our prior written consent.</li>
      <li>Upload malicious code, attempt to disrupt our infrastructure, or bypass authentication or rate-limits.</li>
    </ul>

    <h2>6. Intellectual Property</h2>
    <p>
      All content, trademarks, logos, service marks, models, software and compilations on the Platform are the exclusive property
      of Rev &amp; Dev Children Clinic or its licensors. You are granted a limited, non-exclusive, non-transferable licence to
      use the Platform for personal or internal business purposes strictly in accordance with these Terms. The reports generated
      for you may be shared with your treating physician but may not be republished or redistributed for profit.
    </p>

    <h2>7. Medical Disclaimer</h2>
    <p>
      tots360 is <strong>not a substitute for professional medical advice</strong>. The algorithms and reference datasets used
      (including ICMR-NIN 2020 and WHO growth standards) are based on population-level statistics and may not reflect individual
      clinical circumstances. You acknowledge that any decisions you make based on the reports are at your own risk, and you agree
      to consult a registered medical practitioner before acting on any information presented on the Platform.
    </p>

    <h2>8. Data Protection &amp; Privacy</h2>
    <p>
      Your use of the Platform is also governed by our <a href="/privacy-policy">Privacy Policy</a>, which describes how we collect,
      use, share and protect personal information (including children&rsquo;s health data) in accordance with applicable Indian
      law, including the Digital Personal Data Protection Act, 2023, and the Information Technology Act, 2000.
    </p>

    <h2>9. Refunds &amp; Cancellations</h2>
    <p>
      Refunds and cancellations are governed by our <a href="/refund-policy">Refund &amp; Cancellation Policy</a>.
      Please read it carefully before making a purchase.
    </p>

    <h2>10. Third-Party Services</h2>
    <p>
      The Platform integrates with third-party services such as Razorpay, Stripe, Google Authentication, Resend (email delivery),
      and Google Analytics. We are not responsible for the availability or content of third-party services, and your use of those
      services is at your own risk and subject to their respective terms.
    </p>

    <h2>11. Limitation of Liability</h2>
    <p>
      To the maximum extent permitted by law, Rev &amp; Dev Children Clinic, its directors, employees and affiliates shall not be
      liable for any indirect, incidental, consequential, special or exemplary damages (including loss of profits, data, or
      goodwill) arising out of or relating to your use of the Platform. Our aggregate liability to you for any claim arising under
      these Terms is limited to the amount you paid us for the Service in the twelve (12) months preceding the claim, or ₹10,000
      (whichever is lower).
    </p>

    <h2>12. Indemnification</h2>
    <p>
      You agree to indemnify and hold harmless Rev &amp; Dev Children Clinic, its officers, directors, employees and agents from
      any claim, demand, loss or expense (including reasonable attorneys&rsquo; fees) arising out of or related to your breach of
      these Terms, your misuse of the Platform, or your violation of any law or the rights of a third party.
    </p>

    <h2>13. Termination</h2>
    <p>
      We may suspend or terminate your access to the Platform at any time, without prior notice, if we reasonably believe you have
      violated these Terms, applicable law, or if required to comply with a legal or regulatory obligation. Upon termination, your
      right to use the Service ceases immediately, though provisions of these Terms that by their nature should survive (e.g.,
      intellectual property, limitation of liability, indemnity, dispute resolution) shall continue in force.
    </p>

    <h2>14. Governing Law &amp; Dispute Resolution</h2>
    <p>
      These Terms are governed by and construed in accordance with the laws of India. Any dispute arising out of or relating to these
      Terms shall be subject to the <strong>exclusive jurisdiction of the competent courts in Hyderabad, Telangana, India</strong>.
      Prior to initiating any legal proceeding, the parties shall attempt in good faith to resolve the dispute through amicable
      negotiation for a period of at least 30 days.
    </p>

    <h2>15. Changes to these Terms</h2>
    <p>
      We may update these Terms from time to time. Material changes will be communicated via email or a prominent notice on the
      Platform. Your continued use of the Service after such changes constitutes acceptance of the revised Terms.
    </p>

    <h2>16. Grievance Officer</h2>
    <p>
      In accordance with the Information Technology (Intermediary Guidelines and Digital Media Ethics Code) Rules, 2021, the
      designated Grievance Officer can be contacted at <a href="mailto:info@tots360.in">info@tots360.in</a> or by post at the
      registered office address above. The Grievance Officer will acknowledge receipt of any complaint within 24 hours and
      endeavour to resolve it within 15 days.
    </p>
  </LegalPageLayout>
);

export default TermsPage;
